import { useState, useRef, useEffect } from 'react';
import { FiMic, FiPaperclip, FiSend, FiTrash2 } from 'react-icons/fi';
import GifPicker from './GifPicker';
import useVoiceRecorder, { MAX_VOICE_SECONDS, voiceSupported } from './useVoiceRecorder';
import { formatClock } from './MessageMedia';
import './ChatWidget.css';

// Cloudinary free-plan upload limits.
export const MAX_IMAGE_MB = 10;
export const MAX_VIDEO_MB = 100;

// Width/height (and a video's length) for the bubble's shape while it loads.
export function readMediaInfo(file, localUrl) {
  return new Promise((resolve) => {
    if (file.type.startsWith('video/')) {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () =>
        resolve({ width: v.videoWidth || undefined, height: v.videoHeight || undefined, durationSec: v.duration || undefined });
      v.onerror = () => resolve({});
      v.src = localUrl;
    } else {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({});
      img.src = localUrl;
    }
  });
}

function MessageInput({ onSendMessage, onSendMedia, onTyping, onStopTyping, gifsEnabled = false }) {
  const [message, setMessage] = useState('');
  const [gifOpen, setGifOpen] = useState(false);
  const [error, setError] = useState('');
  const typingTimeoutRef = useRef(null);
  const fileRef = useRef(null);
  const sendingVoiceRef = useRef(false);

  const sendVoice = async () => {
    if (sendingVoiceRef.current) return;
    sendingVoiceRef.current = true;
    try {
      const result = await recorder.stop();
      if (result && result.durationSec >= 0.5 && onSendMedia) {
        onSendMedia({
          type: 'audio',
          file: result.file,
          localUrl: result.localUrl,
          media: { durationSec: result.durationSec, waveform: result.waveform },
        });
      }
    } finally {
      sendingVoiceRef.current = false;
    }
  };
  const recorder = useVoiceRecorder({ onMaxReached: sendVoice });

  const handleChange = (e) => {
    setMessage(e.target.value);
    if (error) setError('');
    if (onTyping) onTyping();
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      if (onStopTyping) onStopTyping();
    }, 1000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
      if (onStopTyping) onStopTyping();
    }
  };

  const startRecording = async () => {
    setError('');
    setGifOpen(false);
    if (!voiceSupported()) {
      setError('Voice messages aren’t supported in this browser.');
      return;
    }
    try {
      await recorder.start();
    } catch (err) {
      setError(
        err && err.name === 'NotAllowedError'
          ? 'Allow microphone access in your browser to record a voice message.'
          : 'Couldn’t start recording. Check your microphone.'
      );
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !onSendMedia) return;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      setError('Send a photo or a video.');
      return;
    }
    const mb = file.size / 1024 / 1024;
    if (isImage && mb > MAX_IMAGE_MB) {
      setError(`Photos can be up to ${MAX_IMAGE_MB} MB.`);
      return;
    }
    if (isVideo && mb > MAX_VIDEO_MB) {
      setError(`Videos can be up to ${MAX_VIDEO_MB} MB.`);
      return;
    }
    setError('');
    const localUrl = URL.createObjectURL(file);
    const media = await readMediaInfo(file, localUrl);
    onSendMedia({ type: isVideo ? 'video' : 'image', file, localUrl, media });
  };

  const pickGif = (g) => {
    setGifOpen(false);
    if (onSendMedia) {
      onSendMedia({ type: 'gif', mediaUrl: g.url, media: { width: g.width, height: g.height } });
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  if (recorder.recording) {
    const nearlyDone = recorder.seconds >= MAX_VOICE_SECONDS - 15;
    return (
      <div className="message-input-form recording">
        <button
          type="button"
          className="composer-icon-btn danger"
          onClick={recorder.cancel}
          title="Discard recording"
          aria-label="Discard recording"
        >
          <FiTrash2 />
        </button>
        <span className="rec-dot" aria-hidden="true" />
        <span className={`rec-time${nearlyDone ? ' warn' : ''}`}>{formatClock(recorder.seconds)}</span>
        <div className="rec-meter" aria-hidden="true">
          {recorder.meter.map((l, i) => (
            <span key={i} style={{ height: `${Math.round(14 + l * 86)}%` }} />
          ))}
        </div>
        <button
          type="button"
          className="message-send-button icon-only"
          onClick={sendVoice}
          title="Send voice message"
          aria-label="Send voice message"
        >
          <FiSend />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="message-input-form">
      {gifOpen && <GifPicker onPick={pickGif} onClose={() => setGifOpen(false)} />}
      {error && <div className="composer-error">{error}</div>}
      {onSendMedia && (
        <>
          <button
            type="button"
            className="composer-icon-btn"
            onClick={() => fileRef.current && fileRef.current.click()}
            title="Send a photo or video"
            aria-label="Send a photo or video"
          >
            <FiPaperclip />
          </button>
          <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={handleFile} />
          {gifsEnabled && (
            <button
              type="button"
              className={`composer-icon-btn gif${gifOpen ? ' active' : ''}`}
              onClick={() => setGifOpen((o) => !o)}
              title="Send a GIF"
              aria-label="Send a GIF"
            >
              GIF
            </button>
          )}
        </>
      )}
      <input
        type="text"
        value={message}
        onChange={handleChange}
        placeholder="Type a message..."
        className="message-input-field"
      />
      {message.trim() || !onSendMedia ? (
        <button type="submit" className="message-send-button">
          Send
        </button>
      ) : (
        <button
          type="button"
          className="composer-mic-btn"
          onClick={startRecording}
          title="Record a voice message"
          aria-label="Record a voice message"
        >
          <FiMic />
        </button>
      )}
    </form>
  );
}

export default MessageInput;
